import { useEffect } from "react";

export interface ContextMenuItem {
  label: string;
  onClick: () => void;
  /** Visual variant — "danger" tints red. */
  variant?: "default" | "danger";
  /** Disabled state — render but ignore clicks. */
  disabled?: boolean;
  /** Insert a divider above this item. */
  separatorAbove?: boolean;
}

interface Props {
  /** Page-coordinate position to anchor the menu's top-left corner. */
  x: number;
  y: number;
  items: ContextMenuItem[];
  onClose: () => void;
}

/**
 * Right-click / long-press context menu. Closes on outside-click, Escape,
 * scroll, or any item click. Auto-flips off-screen edges.
 */
export function CalendarContextMenu({ x, y, items, onClose }: Props) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    const onScroll = () => onClose();
    window.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onScroll, { capture: true });
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onScroll, { capture: true });
    };
  }, [onClose]);

  // Approximate flip — the actual menu height/width is not yet measured
  // when first rendered, but typical sizes give us a reasonable starting
  // estimate. Browsers correct overflow once mounted; we just nudge it
  // off the corners. +6/+6 offset keeps the menu off the click target.
  const estWidth = 200;
  const estHeight = 36 + items.length * 32;
  const offset = 6;
  const adjustedX = Math.min(x + offset, window.innerWidth - estWidth - 8);
  const adjustedY = Math.min(y + offset, window.innerHeight - estHeight - 8);

  return (
    <>
      {/* Click-outside backdrop, transparent */}
      <div
        onClick={onClose}
        onContextMenu={(e) => {
          e.preventDefault();
          onClose();
        }}
        style={{
          position: "fixed",
          inset: 0,
          zIndex: 90,
        }}
      />
      <div
        role="menu"
        style={{
          position: "fixed",
          top: adjustedY,
          left: adjustedX,
          minWidth: 190,
          background: "var(--paper-raised)",
          border: "1px solid var(--rule)",
          boxShadow: "var(--shadow-md)",
          borderRadius: 10,
          padding: 4,
          zIndex: 100,
          fontSize: 13.5,
        }}
      >
        {items.map((item, i) => (
          <div key={i}>
            {item.separatorAbove && (
              <div style={{ height: 1, background: "var(--rule)", margin: "4px 0" }} />
            )}
            <button
              role="menuitem"
              type="button"
              disabled={item.disabled}
              onClick={() => {
                if (item.disabled) return;
                item.onClick();
                onClose();
              }}
              style={{
                display: "block",
                width: "100%",
                textAlign: "left",
                padding: "7px 10px",
                background: "transparent",
                border: "none",
                borderRadius: 6,
                color:
                  item.disabled
                    ? "var(--ink-faint)"
                    : item.variant === "danger"
                      ? "var(--status-cancelled)"
                      : "var(--ink)",
                cursor: item.disabled ? "default" : "pointer",
                font: "inherit",
              }}
              onMouseEnter={(e) => {
                if (!item.disabled) e.currentTarget.style.background = "var(--paper-well)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
              }}
            >
              {item.label}
            </button>
          </div>
        ))}
      </div>
    </>
  );
}
